import { ChangeEvent, useEffect, useMemo, useState } from 'react';
import { Button, Spinner } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { Wrapper } from './clients.styled';
import Search from 'components/forms/Search';
import PaginationComponent from 'components/ui/Pagination';
import NoDataFound from 'components/ui/NoDataFound';
import useDebounce from 'helpers/hooks/useDebounce';
import { useClients } from './controller/client';
import Tabs from 'components/ui/Tabs';
import { TABS } from './consts';
import { loginAsAnotherUser } from 'helpers/http/clients';
import toast from 'react-hot-toast';
import { useNonInitialEffect } from 'helpers/hooks/useNonInitialEffect';
import { ManageColumns } from 'components/ui/ManageColumns';
import {
  ColumnOrderState,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { updateColumns } from 'helpers/http/common';
import { clientTableColumns } from 'pages/clients/partials/clientTableColumns';
import { CustomTable } from 'components/ui/CustomTable';
import { editUser } from 'helpers/http/freelancer';

const Clients = () => {
  const navigate = useNavigate();
  /*
   * This will fetch all the clients with the searched keyword
   */
  const {
    filters,
    updateFilters,
    isLoading,
    isRefetching,
    data,
    totalPages,
    active,
    deleted,
    flagged,
    refetch,
  } = useClients();
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoadingManageColumns, setIsLoadingManageColumns] = useState(false);
  const [columnVisibility, setColumnVisibility] = useState({});
  const [columnOrder, setColumnOrder] = useState<ColumnOrderState>([]);
  const [selectedId, setSelectedId] = useState('');

  const debouncedSearchQuery = useDebounce(searchTerm, 500);

  const onSearch = (e: ChangeEvent<HTMLInputElement>) =>
    setSearchTerm(e.target.value);

  useEffect(() => {
    setSearchTerm(filters.keyword);
  }, [filters.keyword]);

  useNonInitialEffect(() => {
    updateFilters({
      keyword: debouncedSearchQuery?.trim(),
      page: 1,
      searchBy: '',
      searchText: '',
    });
  }, [debouncedSearchQuery, updateFilters]);

  // gettings freelancer columns settings from user object and updating state
  useEffect(() => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    if (user?.client_columns?.column_visibility) {
      setColumnVisibility(user.client_columns.column_visibility);
    }
    if (user?.client_columns?.column_order) {
      setColumnOrder(user.client_columns.column_order);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onPageChange = (page: { selected: number }) =>
    updateFilters({ page: page?.selected + 1 });

  const onTabChange = (tab: string) => {
    updateFilters({ status: tab, page: 1 });
  };

  const goToClientProfile = (id: string) => {
    // redirect user to client profile
    navigate(`/client-profile/${id}`);
  };
  const onLoginAsAnotherUser = (user_id: string) => () => {
    const promise = loginAsAnotherUser(user_id);

    toast.promise(promise, {
      loading: 'Loading...',
      success: (res) => {
        if (window.location.host.includes('beta')) {
          window.open(
            `https://beta.zehmizeh.com/login?key=${user_id}&token=${res.data.token}`
          );
        } else {
          window.open(
            `https://www.zehmizeh.com/login?key=${user_id}&token=${res.data.token}`
          );
        }
        return res.message;
      },
      error: (err) => {
        return err?.response?.data?.message || 'error';
      },
    });
  };

  const onFlagChange = (id: string, flag: 0 | 1) => () => {
    setSelectedId(id);

    const promise = editUser({
      user_id: id,
      is_flagged: flag,
    });

    toast.promise(promise, {
      loading: 'Loading...',
      success: (res) => {
        refetch();
        setSelectedId('');
        return res.message;
      },
      error: (err) => {
        setSelectedId('');
        return err?.response?.data?.message || 'error';
      },
    });
  };

  const memoizedClientsTableColumns = useMemo(
    () =>
      clientTableColumns(
        onLoginAsAnotherUser,
        onFlagChange,
        selectedId,
        filters,
        updateFilters
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedId, filters]
  );

  const table = useReactTable({
    data: data || [],
    columns: memoizedClientsTableColumns,
    state: {
      columnVisibility,
      columnOrder,
    },
    getCoreRowModel: getCoreRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onColumnOrderChange: setColumnOrder,
  });

  const handleColumnsSave = () => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const payload = {
      client_columns: {
        column_visibility: table?.options?.state?.columnVisibility || {},
        column_order: table?.options?.state?.columnOrder || [],
      },
    };

    // No need to make api call if user haven't changed settings
    if (
      JSON.stringify(user?.client_columns) ===
      JSON.stringify(payload.client_columns)
    )
      return;

    setIsLoadingManageColumns(true);
    updateColumns(payload)
      .then(() => {
        setIsLoadingManageColumns(false);
        toast.success('Updated column settings');
        // updating it in localstorage because user object updates only on logout and login
        localStorage.setItem('user', JSON.stringify({ ...user, ...payload }));
      })
      .catch((err) => {
        setIsLoadingManageColumns(false);
        console.error('Update columns:', err);
        toast.error('Failed to update column settings');
      });
  };

  const resetFilter = () => {
    updateFilters({
      keyword: '',
      page: 1,
      sortBy: '',
      searchBy: '',
      sortOrder: '',
      searchText: '',
    });
  };

  return (
    <Wrapper>
      <div className="d-flex align-items-center my-3">
        <h1 className="fs-32 fw-700 mb-0">Clients</h1>
        {(isLoading || isRefetching) && (
          <Spinner animation="border" className="ms-2" size="sm" />
        )}
      </div>

      {/* Search box and status filters*/}
      <div className="d-flex align-items-center justify-content-between flex-wrap gap-3">
        {/* Search box */}
        <Search value={searchTerm} onChange={onSearch} />

        {/* Filters */}
        <div className="d-flex flex-direction-row align-items-center">
          <Tabs
            tabs={TABS}
            activeTab={filters?.status || TABS[0].key}
            onTabChange={onTabChange}
            counts={{
              all: Number(active) + Number(deleted),
              active: active,
              deleted: deleted,
              flagged: flagged,
            }}
          />
          {data && data?.length > 0 && (
            <ManageColumns
              elementId="clients-table-manage-columns"
              handleSave={handleColumnsSave}
              className="ms-4"
              table={table}
              isLoading={isLoadingManageColumns}
            />
          )}
        </div>
      </div>

      {/* Listing table */}
      <div className="listing-table p-3 mt-4">
        <div className="listings">
          {data && data?.length > 0 && (
            <CustomTable
              table={table}
              handleRowClick={(row) => {
                if (row.original?.user_id)
                  goToClientProfile(row.original.user_id);
              }}
            />
          )}
          <div id="more-button-dropdown-menu"></div>
          {/* No data found */}
          {!isLoading && !isRefetching && data?.length == 0 && (
            <div className="my-5 py-5">
              <NoDataFound />
              {/* START ----------------------------------------- Showing reset filter button only if any filter is applied */}
              {[
                'sortBy',
                'sortOrder',
                'searchBy',
                'searchText',
                'keyword',
              ].some((key) => filters[key]) && (
                <div className="d-flex justify-content-center mt-2">
                  <Button className="text-center" onClick={resetFilter}>
                    Reset Filters
                  </Button>
                </div>
              )}
              {/* END ------------------------------------------- Showing reset filter button only if any filter is applied */}
            </div>
          )}
        </div>

        {/* Pagination */}
        {data && data?.length > 0 && (
          <div className="d-flex justify-content-center mt-3">
            <PaginationComponent
              total={totalPages}
              onPageChange={onPageChange}
              currentPage={filters.page}
            />
          </div>
        )}
      </div>
    </Wrapper>
  );
};

export default Clients;
